import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ArrowRight, PlayCircle, Radio, FileText, ShoppingBag, GraduationCap, TrendingUp, Award, Users, BookOpen, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import CourseCard from "@/components/CourseCard";

function BannerSlider({ slides }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();
  const n = slides.length;
  useEffect(() => {
    if (n < 2 || paused) return undefined;
    const t = setInterval(() => setI((p) => (p + 1) % n), 5000);
    return () => clearInterval(t);
  }, [n, paused]);
  const go = (d) => setI((p) => (p + d + n) % n);
  return (
    <section className="bg-[#F7F7F8] pt-3 pb-4" data-testid="banner-slider" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="relative max-w-[1600px] mx-auto overflow-hidden md:rounded-xl">
        <div className="flex transition-transform duration-700 ease-out" style={{ transform: `translateX(-${i * 100}%)` }}>
          {slides.map((s, k) => (
            <div key={s.id} className="relative w-full shrink-0 h-[230px] sm:h-[300px] md:h-[380px] cursor-pointer" onClick={() => navigate(s.url || "/")} data-testid={`banner-slide-${k}`}>
              <img src={s.image} alt={s.title} className="absolute inset-0 h-full w-full object-cover object-right" loading={k ? "lazy" : "eager"} />
              <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/10 md:to-transparent md:via-white/60" />
              <div className="relative h-full max-w-7xl mx-auto px-6 sm:px-12 flex flex-col justify-center">
                {s.eyebrow && <span className="text-crimson font-bold text-[11px] sm:text-xs tracking-widest uppercase">{s.eyebrow}</span>}
                <h2 className="font-heading font-black text-navy text-2xl sm:text-4xl lg:text-5xl leading-[1.05] mt-2 max-w-[14ch] sm:max-w-xl">{s.title}</h2>
                {s.subtitle && <p className="text-navy/70 text-sm mt-2 max-w-md">{s.subtitle}</p>}
                {s.points?.length > 0 && <ul className="hidden sm:flex flex-wrap gap-x-6 gap-y-1 mt-4">{s.points.map((p) => <li key={p} className="flex items-center gap-1.5 text-sm md:text-base font-semibold text-crimson"><Check size={18} strokeWidth={3} className="text-navy" />{p}</li>)}</ul>}
                <Button className="mt-5 w-fit rounded-full viriks-amber text-white font-bold h-10 sm:h-12 px-6 sm:px-8 sm:text-base hover:scale-[1.03] transition-transform" onClick={(e) => { e.stopPropagation(); navigate(s.url || "/"); }} data-testid={`banner-cta-${k}`}>{s.cta || "Join Now"} <ChevronRight size={18} className="ml-1" /></Button>
              </div>
            </div>
          ))}
        </div>
        {n > 1 && <>
          <button onClick={() => go(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 hidden sm:flex rounded-full bg-white/80 hover:bg-white shadow flex items-center justify-center text-navy" aria-label="Previous banner" data-testid="banner-prev"><ChevronLeft size={18} /></button>
          <button onClick={() => go(1)} className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 hidden sm:flex rounded-full bg-white/80 hover:bg-white shadow flex items-center justify-center text-navy" aria-label="Next banner" data-testid="banner-next"><ChevronRight size={18} /></button>
        </>}
      </div>
      {n > 1 && <div className="flex justify-center gap-2 mt-3">{slides.map((s, k) => <button key={s.id} onClick={() => setI(k)} aria-label={`Banner ${k + 1}`} className={`h-2 rounded-full transition-[width,background-color] duration-300 ${k === i ? "w-6 bg-crimson" : "w-2 bg-gray-300 hover:bg-gray-400"}`} data-testid={`banner-dot-${k}`} />)}</div>}
    </section>
  );
}

function HeroSlider({ slides }) {
  const [i] = useState(0);
  const navigate = useNavigate();
  const s = slides[i] || { title: "Learn. Grow. Succeed.", subtitle: "Recorded courses, live classes and test series — all in one place with VIRIKS.", url: "/courses" };
  return (
    <section className="relative viriks-navy text-white overflow-hidden" data-testid="hero-slider">
      <div className="absolute inset-0 hero-noise opacity-40" />
      <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-amber/20 blur-3xl" />
      <div className={`max-w-7xl mx-auto px-6 py-16 md:py-24 grid gap-10 items-center relative ${s.image ? "md:grid-cols-2" : "max-w-4xl"}`}>
        <div className="fade-up" key={i}>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black leading-[1.05]">{s.title}</h1>
          <p className="mt-5 text-lg text-white/70 max-w-lg">{s.subtitle}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" className="rounded-full viriks-amber text-white font-semibold hover:bg-amber/90 hover:scale-[1.02] transition-transform" onClick={() => navigate(s.url || "/courses")} data-testid="hero-cta">
              Explore Courses <ArrowRight size={18} className="ml-1" />
            </Button>
            <Button size="lg" variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10" onClick={() => navigate("/register")}>
              Start Free
            </Button>
          </div>
        </div>
        {s.image && (
          <div className="relative hidden md:block fade-up">
            <img src={s.image} alt="hero" className="rounded-2xl w-full h-[380px] object-cover shadow-2xl" />
          </div>
        )}
      </div>
    </section>
  );
}

const features = [
  { icon: PlayCircle, title: "Recorded Courses", desc: "Learn anytime with HD video lectures & notes" },
  { icon: Radio, title: "Live Classes", desc: "Interactive live sessions with top educators" },
  { icon: FileText, title: "Test Series", desc: "Full-length mocks with detailed analysis" },
  { icon: ShoppingBag, title: "VIRIKS Store", desc: "Books, notes & study material delivered" },
];

export default function Home() {
  const [slides, setSlides] = useState([]);
  const [courses, setCourses] = useState([]);
  const [cats, setCats] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/banners?platform=web&btype=slider").then(({ data }) => setSlides(data)).catch(() => {});
    api.get("/courses?sort=popular").then(({ data }) => setCourses(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {slides.length > 0 ? <BannerSlider slides={slides} /> : <HeroSlider slides={[]} />}

      {cats.length > 0 && <section className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="font-heading text-3xl font-bold text-navy mb-8">Explore Courses</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {cats.map((c) => (
            <Link key={c.id} to={`/courses?category=${c.id}`} className="bg-white rounded-xl border border-border p-5 text-center hover:-translate-y-1 hover:shadow-lg transition-all" data-testid={`category-${c.id}`}>
              <div className="h-12 w-12 rounded-xl viriks-navy text-amber mx-auto flex items-center justify-center mb-3"><BookOpen size={22} /></div>
              <div className="font-semibold text-sm text-navy">{c.name}</div>
            </Link>
          ))}
        </div>
      </section>}

      {/* Learn With VIRIKS */}
      <section className="viriks-navy text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <h2 className="font-heading text-3xl font-bold mb-3">Learn With VIRIKS</h2>
          <p className="text-white/60 mb-10 max-w-xl">Everything you need to succeed, in one powerful platform.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f) => (
              <div key={f.title} className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors">
                <div className="h-12 w-12 rounded-xl viriks-amber text-white flex items-center justify-center mb-4"><f.icon size={22} /></div>
                <h3 className="font-heading font-semibold text-lg">{f.title}</h3>
                <p className="text-white/60 text-sm mt-1">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {courses.length > 0 && <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-heading text-3xl font-bold text-navy">Trending Courses</h2>
          <Link to="/courses" className="text-amber font-semibold flex items-center gap-1 hover:gap-2 transition-all">View all <ArrowRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {courses.slice(0, 8).map((c) => <CourseCard key={c.id} course={c} />)}
        </div>
      </section>}

      {/* CTA Banners */}
      <section className="max-w-7xl mx-auto px-6 py-8 grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl p-8 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg,#111111,#2b2b3a)" }} data-testid="become-educator-cta">
          <GraduationCap size={120} className="absolute -right-4 -bottom-4 text-white/5" />
          <Award className="text-amber mb-3" size={28} />
          <h3 className="font-heading text-2xl font-bold">Become an Educator</h3>
          <p className="text-white/70 mt-2 max-w-sm">Share your knowledge, teach thousands and earn. Create courses, host live classes & build your brand.</p>
          <Button className="mt-6 rounded-full viriks-amber text-white font-semibold" onClick={() => navigate("/register?role=educator")}>Start Teaching <ArrowRight size={16} className="ml-1" /></Button>
        </div>
        <div className="rounded-2xl p-8 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg,#ED1C24,#8f0d13)" }} data-testid="become-seller-cta">
          <Users size={120} className="absolute -right-4 -bottom-4 text-white/5" />
          <TrendingUp className="text-white mb-3" size={28} />
          <h3 className="font-heading text-2xl font-bold">Become a Content Seller</h3>
          <p className="text-white/80 mt-2 max-w-sm">Refer students, earn up to 20% commission on every sale. Get your unique referral link & wallet.</p>
          <Button className="mt-6 rounded-full bg-white text-crimson font-semibold hover:bg-white/90" onClick={() => navigate("/register?role=content_seller")}>Join as Seller <ArrowRight size={16} className="ml-1" /></Button>
        </div>
      </section>

    </div>
  );
}
